import { useQuery } from '@tanstack/react-query';
import { request } from './api.js';
import { getTenantSlug } from './tenant.js';
import type { Capability, Locale } from '@monrdv/shared';

/** A per-tenant label, as stored: the same shape as pipeline stage names. */
type Translatable = Record<Locale, string>;

/**
 * The signed-in user, and what they may do in the tenant they are currently acting in.
 *
 * Capabilities come from the server, in the session payload — **not** from a table of roles
 * in the client. Roles are editable per business, so a client-side map would be wrong the
 * moment somebody changes one, and it would be wrong in the direction of showing actions the
 * API then refuses.
 *
 * Hiding is a courtesy, not a control: the server is the authority and every endpoint checks
 * independently.
 */
export type Session = {
  userId: string;
  email: string;
  name: string;
  locale: Locale;
  tenantId: string;
  /** The business slug, which is in the URL of every page and every API call. */
  tenantSlug: string | null;
  tenantName: string;
  brandColor: string;
  role: { id: string; key: string; name: Translatable; landing: 'portal' | 'field' };
  /** What this person may do here, resolved from their role row on the server. */
  capabilities: Capability[];
  /** Every tenant this person can switch to without signing in again. */
  tenants: Array<{ id: string; slug: string; name: string; brandColor: string; roleName: Translatable }>;
};

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    /*
     * The tab's own business is named in the request, so the name, colour and capabilities
     * that come back belong to what this tab is showing rather than to whatever another tab
     * last selected.
     */
    queryFn: () => {
      const slug = getTenantSlug();
      return request<Session>(`/auth/session${slug ? `?tenant=${encodeURIComponent(slug)}` : ''}`);
    },
    // The session rarely changes and every screen needs it; refetching it on every mount
    // would add a request to every navigation for no benefit.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Capability check for the current session. Returns false while the session is loading. */
export function useCan(): (capability: Capability) => boolean {
  const session = useSession();
  const granted = session.data?.capabilities;
  // Denies while the session is loading: showing an action and then removing it is worse
  // than showing it a moment late.
  return (capability) => (granted ? granted.includes(capability) : false);
}
